import { useState } from 'react'
import type { PlanMeta } from '../api'

// PlanSwitcher lives in its own module so the console can import it WITHOUT
// pulling in PlanningView, which carries every fullscreen artifact body. One
// small static import from a large module pins that whole module into the
// initial bundle, and this was doing exactly that: ~80KB of panels parsed on
// every cold load to render a dropdown.

function timeAgo(iso?: string): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const s = Math.max(0, (Date.now() - t) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

// PlanSwitcher is the plan-library context bar pinned at the top of the Planning view:
// the active plan is the context everything below belongs to. Click to open the library
// (all saved plans), switch, rename inline, delete, or start a new one.
export function PlanSwitcher({
  plans,
  activeId,
  activeTaskCount,
  busy,
  composing,
  onSwitch,
  onRename,
  onSetClient,
  onDelete,
  onNew,
}: {
  plans: PlanMeta[]
  activeId?: string
  // Live task count of the active plan (board.tasks.length). The plans-list `task_count`
  // is meta that can lag the live board after an edit — for the plan you're actually
  // looking at, show the live count so it never disagrees with the header/task board.
  activeTaskCount?: number
  busy: boolean
  composing?: boolean
  onSwitch: (id: string) => void
  onRename: (id: string, title: string) => void
  onSetClient: (id: string, client: string) => void
  onDelete: (id: string) => void
  onNew: () => void
}) {
  const countFor = (pm: PlanMeta) => (pm.id === activeId && activeTaskCount != null ? activeTaskCount : pm.task_count)
  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [clientFilter, setClientFilter] = useState('all')
  const [query, setQuery] = useState('')
  const active = plans.find((p) => p.id === activeId) ?? plans.find((p) => p.active)
  const clients = [...new Set(plans.map((p) => p.client).filter((c): c is string => !!c))].sort()
  const q = query.trim().toLowerCase()
  // Most recently edited first — the plan you were just working on is almost
  // always the one you're looking for. Search matches title or client.
  const shown = plans
    .filter((p) => clientFilter === 'all' || p.client === clientFilter)
    .filter((p) => !q || p.title.toLowerCase().includes(q) || (p.client ?? '').toLowerCase().includes(q))
    .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''))

  const commitRename = (id: string) => {
    const v = editValue.trim()
    if (v) onRename(id, v)
    setEditingId(null)
  }

  return (
    <div className="planswitch">
      <button className="planswitch-trigger" onClick={() => setOpen((o) => !o)} disabled={busy}>
        <span className="planswitch-label">Plan</span>
        <span className="planswitch-title">{composing ? 'New plan (composing…)' : active?.title ?? 'Untitled plan'}</span>
        <span className="planswitch-count">
          {composing ? '' : active ? `${countFor(active)} task${countFor(active) === 1 ? '' : 's'}` : ''}
        </span>
        <span className={`planswitch-chev ${open ? 'up' : ''}`}>▾</span>
      </button>
      {open ? (
        <>
          <div className="planswitch-backdrop" onClick={() => setOpen(false)} />
          <div className="planswitch-menu">
            <div className="planswitch-menu-head">
              Plans <span className="state">{shown.length}{clientFilter !== 'all' || q ? ` of ${plans.length}` : ''} saved</span>
              {clients.length > 0 ? (
                <select
                  className="planswitch-clientfilter"
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  title="Filter by client"
                >
                  <option value="all">all clients</option>
                  {clients.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              ) : null}
            </div>
            <button
              className="planswitch-new"
              onClick={() => {
                onNew()
                setOpen(false)
              }}
            >
              ＋ New plan
            </button>
            <input
              className="planswitch-search"
              autoFocus
              placeholder="Search plans (title or client)…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  if (query) setQuery('')
                  else setOpen(false)
                }
                if (e.key === 'Enter' && shown.length > 0 && shown[0]) {
                  onSwitch(shown[0].id)
                  setOpen(false)
                }
              }}
            />
            <div className="planswitch-list">
              {shown.map((p) => (
                <div key={p.id} className={`planswitch-row ${p.id === activeId ? 'active' : ''}`}>
                  {editingId === p.id ? (
                    <input
                      className="planswitch-edit"
                      autoFocus
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename(p.id)
                        if (e.key === 'Escape') setEditingId(null)
                      }}
                      onBlur={() => commitRename(p.id)}
                    />
                  ) : (
                    <button
                      className="planswitch-rowmain"
                      onClick={() => {
                        onSwitch(p.id)
                        setOpen(false)
                      }}
                    >
                      <span className="planswitch-rowtitle">{p.title}</span>
                      <span className="planswitch-rowmeta">
                        {p.client ? `${p.client} · ` : ''}{countFor(p)} task{countFor(p) === 1 ? '' : 's'}
                        {p.updated_at ? ` · ${timeAgo(p.updated_at)}` : ''}
                      </span>
                    </button>
                  )}
                  <div className="planswitch-rowactions">
                    <button
                      title="Rename"
                      onClick={() => {
                        setEditingId(p.id)
                        setEditValue(p.title)
                      }}
                    >
                      ✎
                    </button>
                    <button
                      title={p.client ? `Client: ${p.client} (click to change)` : 'Tag with a client'}
                      onClick={() => {
                        const v = prompt('Client / branch label (blank clears):', p.client ?? '')
                        if (v !== null) onSetClient(p.id, v.trim())
                      }}
                    >
                      🏷
                    </button>
                    <button
                      title="Delete"
                      className="del"
                      onClick={() => {
                        if (confirm(`Delete plan "${p.title}"? This can't be undone.`)) onDelete(p.id)
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
